import { useQuery } from '@tanstack/react-query';
import type { MediaRef, SeoMeta, TranslatableString } from '@geoic/shared';
import { Card, CardBody, CardHeader } from '@/components/ui/Card.js';
import { Checkbox, Input } from '@/components/ui/Field.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import { RichTextInput } from '@/components/form/RichTextInput.js';
import { MediaPicker } from '@/components/media/MediaPicker.js';
import { MediaGalleryPicker } from '@/components/media/MediaGalleryPicker.js';
import {
  EnumSelect,
  MultiSelect,
  Repeater,
  emptyTranslatable,
} from '@/features/blocks/BlockFields.js';
import { queryKeys } from '@/lib/query-keys.js';
import { dateOnly, price as formatPrice } from '@/lib/format.js';
import { ContentListPage } from '../ContentListPage.js';
import { ContentEditPage } from '../ContentEditPage.js';
import { SeoPanel, emptySeo } from '../SeoPanel.js';
import { createContentApi, type ContentRecord } from '../content-api.js';
import { staffApi } from '../staff/StaffPages.js';

type ProgramItem = {
  title: TranslatableString;
  content: TranslatableString;
  hours: number | null;
  order: number;
};

type ScheduleItem = {
  startDate: string;
  seatsTotal: number | null;
  seatsLeft: number | null;
  status: 'open' | 'full' | 'closed';
};

export type CourseRecord = ContentRecord & {
  title: TranslatableString;
  description: TranslatableString;
  program: ProgramItem[];
  durationHours: number | null;
  price: number | null;
  level: 'beginner' | 'advanced' | 'professional';
  certificate: boolean;
  instructorIds: string[];
  coverImage: MediaRef | null;
  gallery: MediaRef[];
  schedule: ScheduleItem[];
  seo: SeoMeta;
};

export const coursesApi = createContentApi<CourseRecord>('/courses');

const LEVELS = [
  { value: 'beginner' as const, label: 'Boshlang‘ich' },
  { value: 'advanced' as const, label: 'Kengaytirilgan' },
  { value: 'professional' as const, label: 'Professional' },
];

export function CoursesListPage() {
  return (
    <ContentListPage<CourseRecord>
      title="Kurslar"
      collection="courses"
      contentApi={coursesApi}
      basePath="/courses"
      createLabel="Kurs qo‘shish"
      sortable
      emptyTitle="Hozircha kurs yo‘q"
      columns={[
        {
          key: 'title',
          header: 'Nomi',
          render: (row) => (
            <span className="font-medium text-n-800">{row.title?.uz || '(nomsiz)'}</span>
          ),
        },
        {
          key: 'price',
          header: 'Narx',
          align: 'right',
          width: '160px',
          hideOnTablet: true,
          render: (row) => <span className="text-n-600">{formatPrice(row.price)}</span>,
        },
        {
          key: 'schedule',
          header: 'Guruhlar',
          align: 'right',
          width: '100px',
          hideOnTablet: true,
          render: (row) => <span className="text-n-600">{row.schedule?.length ?? 0}</span>,
        },
      ]}
    />
  );
}

type CourseForm = {
  title: TranslatableString;
  slug: string;
  description: TranslatableString;
  program: ProgramItem[];
  durationHours: string;
  price: string;
  level: 'beginner' | 'advanced' | 'professional';
  certificate: boolean;
  instructorIds: string[];
  coverImage: MediaRef | null;
  gallery: MediaRef[];
  schedule: ScheduleItem[];
  seo: SeoMeta;
};

export function CourseEditPage() {
  const params = { limit: 100, sort: 'title' as const };
  const instructors = useQuery({
    queryKey: queryKeys.content.list('staff', { ...params, purpose: 'options' }),
    queryFn: ({ signal }) => staffApi.list(params, signal),
    staleTime: 5 * 60 * 1000,
  });

  const instructorOptions = (instructors.data?.data ?? []).map((row) => ({
    value: row._id,
    label: row.fullName?.uz || row.slug || row._id,
  }));

  return (
    <ContentEditPage<CourseForm>
      collection="courses"
      contentApi={coursesApi}
      basePath="/courses"
      backLabel="Kurslar"
      newTitle="Yangi kurs"
      titleOf={(form) => form.title.uz}
      empty={() => ({
        title: emptyTranslatable(),
        slug: '',
        description: emptyTranslatable(),
        program: [],
        durationHours: '',
        price: '',
        level: 'beginner',
        certificate: true,
        instructorIds: [],
        coverImage: null,
        gallery: [],
        schedule: [],
        seo: emptySeo(),
      })}
      fromRecord={(record) => {
        const course = record as CourseRecord;
        return {
          title: course.title ?? emptyTranslatable(),
          slug: course.slug ?? '',
          description: course.description ?? emptyTranslatable(),
          program: course.program ?? [],
          durationHours: course.durationHours === null ? '' : String(course.durationHours ?? ''),
          price: course.price === null ? '' : String(course.price ?? ''),
          level: course.level ?? 'beginner',
          certificate: course.certificate ?? true,
          instructorIds: course.instructorIds ?? [],
          coverImage: course.coverImage ?? null,
          gallery: course.gallery ?? [],
          schedule: (course.schedule ?? []).map((item) => ({
            ...item,
            startDate: item.startDate ? String(item.startDate).slice(0, 10) : '',
          })),
          seo: course.seo ?? emptySeo(),
        };
      }}
      toInput={(form) => ({
        title: form.title,
        slug: form.slug,
        description: form.description,
        program: form.program.map((item, index) => ({ ...item, order: index })),
        durationHours: form.durationHours === '' ? null : Number(form.durationHours),
        // Bo'sh narx — «Narxni so'rang» (ADR-004). `0` esa «Bepul» degani,
        // shuning uchun bo'sh satr bilan nolni farqlash SHART.
        price: form.price === '' ? null : Number(form.price),
        level: form.level,
        certificate: form.certificate,
        instructorIds: form.instructorIds,
        coverImage: form.coverImage,
        gallery: form.gallery,
        schedule: form.schedule.filter((item) => item.startDate !== ''),
        seo: form.seo,
      })}
      renderFields={({ form, setForm, fieldErrors }) => (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <Card>
              <CardHeader title="Kurs" />
              <CardBody className="space-y-4">
                <TranslatableInput
                  label="Nomi"
                  required
                  value={form.title}
                  onChange={(title) => setForm((current) => ({ ...current, title }))}
                  error={fieldErrors['title.uz']}
                />
                <RichTextInput
                  label="Tavsif"
                  value={form.description}
                  onChange={(description) => setForm((current) => ({ ...current, description }))}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Dastur" description="Kurs mavzulari ketma-ketligi" />
              <CardBody>
                <Repeater
                  label="Mavzular"
                  max={40}
                  addLabel="Mavzu qo‘shish"
                  items={form.program}
                  getId={(_, index) => `topic-${index}`}
                  summary={(item, index) => item.title.uz || `${index + 1}-mavzu`}
                  create={() => ({
                    title: emptyTranslatable(),
                    content: emptyTranslatable(),
                    hours: null,
                    order: form.program.length,
                  })}
                  onChange={(program) => setForm((current) => ({ ...current, program }))}
                  renderItem={(item, update) => (
                    <>
                      <TranslatableInput
                        label="Mavzu"
                        value={item.title}
                        onChange={(title) => update({ ...item, title })}
                      />
                      <TranslatableInput
                        label="Tarkib"
                        multiline
                        rows={3}
                        value={item.content}
                        onChange={(content) => update({ ...item, content })}
                      />
                      <Input
                        type="number"
                        label="Soat"
                        min={0}
                        value={item.hours ?? ''}
                        onChange={(event) =>
                          update({
                            ...item,
                            hours: event.target.value === '' ? null : Number(event.target.value),
                          })
                        }
                      />
                    </>
                  )}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="Guruhlar jadvali"
                description="Sayt shu ro‘yxatdan «yaqin guruh» ni oladi"
              />
              <CardBody>
                <Repeater
                  label="Guruhlar"
                  max={40}
                  addLabel="Guruh qo‘shish"
                  items={form.schedule}
                  getId={(_, index) => `group-${index}`}
                  summary={(item, index) =>
                    item.startDate ? dateOnly(item.startDate) : `${index + 1}-guruh`
                  }
                  create={() => ({
                    startDate: '',
                    seatsTotal: null,
                    seatsLeft: null,
                    status: 'open' as const,
                  })}
                  onChange={(schedule) => setForm((current) => ({ ...current, schedule }))}
                  renderItem={(item, update) => (
                    <>
                      <Input
                        type="date"
                        label="Boshlanish sanasi"
                        value={item.startDate}
                        onChange={(event) => update({ ...item, startDate: event.target.value })}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          type="number"
                          label="Jami o‘rin"
                          min={0}
                          value={item.seatsTotal ?? ''}
                          onChange={(event) =>
                            update({
                              ...item,
                              seatsTotal:
                                event.target.value === '' ? null : Number(event.target.value),
                            })
                          }
                        />
                        <Input
                          type="number"
                          label="Bo‘sh o‘rin"
                          min={0}
                          value={item.seatsLeft ?? ''}
                          onChange={(event) =>
                            update({
                              ...item,
                              seatsLeft:
                                event.target.value === '' ? null : Number(event.target.value),
                            })
                          }
                        />
                      </div>
                      <EnumSelect
                        label="Holat"
                        value={item.status}
                        options={[
                          { value: 'open', label: 'Qabul ochiq' },
                          { value: 'full', label: 'O‘rinlar to‘lgan' },
                          { value: 'closed', label: 'Qabul yopiq' },
                        ]}
                        onChange={(status) => update({ ...item, status })}
                      />
                    </>
                  )}
                />
              </CardBody>
            </Card>

            <SeoPanel
              value={form.seo}
              onChange={(seo) => setForm((current) => ({ ...current, seo }))}
              fallbackTitle={form.title.uz || undefined}
            />
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader title="Sozlamalar" />
              <CardBody className="space-y-4">
                <MediaPicker
                  label="Muqova rasmi"
                  value={form.coverImage}
                  onChange={(coverImage) => setForm((current) => ({ ...current, coverImage }))}
                />
                <Input
                  type="number"
                  label="Narx (so‘m)"
                  min={0}
                  value={form.price}
                  placeholder="Bo‘sh — «Narxni so‘rang»"
                  onChange={(event) =>
                    setForm((current) => ({ ...current, price: event.target.value }))
                  }
                  hint="0 kiritilsa «Bepul» deb ko‘rsatiladi"
                />
                <Input
                  type="number"
                  label="Davomiyligi (soat)"
                  min={0}
                  value={form.durationHours}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, durationHours: event.target.value }))
                  }
                />
                <EnumSelect
                  label="Daraja"
                  value={form.level}
                  options={LEVELS}
                  onChange={(level) => setForm((current) => ({ ...current, level }))}
                />
                <MultiSelect
                  label="O‘qituvchilar"
                  max={10}
                  selected={form.instructorIds}
                  options={instructorOptions}
                  onChange={(instructorIds) =>
                    setForm((current) => ({ ...current, instructorIds }))
                  }
                  hint="Rahbariyat bo‘limidagi xodimlardan tanlanadi"
                />
                <Checkbox
                  label="Sertifikat beriladi"
                  checked={form.certificate}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, certificate: event.target.checked }))
                  }
                />
                <Input
                  label="Manzil (slug)"
                  value={form.slug}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, slug: event.target.value }))
                  }
                  error={fieldErrors.slug}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Galereya" />
              <CardBody>
                <MediaGalleryPicker
                  label="Rasmlar"
                  value={form.gallery}
                  onChange={(gallery) => setForm((current) => ({ ...current, gallery }))}
                />
              </CardBody>
            </Card>
          </div>
        </div>
      )}
    />
  );
}
